/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { request } from '@/services/request';
import Alert from 'public/shared-icons/Alert.svg';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { CustomButton } from '../common';
import Loader from '../spinner/loader';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export const DeleteDialog = ({
  open,
  setOpen,
  title,
  name,
  isWorkflow,
  // refresh,
  dataLen,
  showRadio,
  refreshComponents,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  name: string;
  isWorkflow?: boolean;
  dataLen?: number;
  refresh?: () => Promise<void>;
  showRadio?: boolean;
  refreshComponents?: () => Promise<void>;
}) => {
  const route = useRouter();

  const [loading, setLoading] = useState(false);
  const { locale, messages } = useLanguage();
  const langData = messages?.DeleteDialog;

  const [radioSelected, setRadioSelected] = useState('delete only app');

  // Helper function to update localStorage
  const updateLocalStorageAfterDeletion = (deletedAppName: string) => {
    try {
      // Update measuresList
      const cardMeasuresData = localStorage.getItem('measuresList');
      if (cardMeasuresData) {
        const measuresArray = JSON.parse(cardMeasuresData);

        const updatedMeasures = { ...measuresArray };
        if (updatedMeasures[deletedAppName]) {
          delete updatedMeasures[deletedAppName];
        }
        localStorage.setItem('measuresList', JSON.stringify(updatedMeasures));
      }

      // Update appsList
      const storedAppsList = localStorage.getItem('appsList');
      if (storedAppsList) {
        const parsedList = JSON.parse(storedAppsList);
        // Ensure it's an array, converting from an array-like object if necessary
        const appsArray = Array.isArray(parsedList)
          ? parsedList
          : Object.values(parsedList);
        // Filter out items that match the deleted app name
        const updatedApps = appsArray.filter(
          (app: string) => app !== deletedAppName
        );
        localStorage.setItem('appsList', JSON.stringify(updatedApps));
      }
    } catch (error) {
      console.error('Error updating localStorage:', error);
    }
  };

  const handleDelete = async () => {
    if (radioSelected === 'delete only app') {
      try {
        setLoading(true);
        if (isWorkflow) {
          await request.delete({ endpoint: `components/${name}` });
          toast.success(langData?.component_deleted_successfully);
          // update list after delete
          await refreshComponents?.();
          route.push(`/${locale}/components`);
        } else {
          await request.delete({ endpoint: `apps/${name}` });
          toast.success(langData?.application_deleted_successfully);

          // Update localStorage after successful deletion
          updateLocalStorageAfterDeletion(name);

          route.push(`/${locale}/apps`);
        }
      } catch (error) {
        if (isWorkflow) {
          toast.error(langData?.error_in_deleting_component);
        } else {
          toast.error(langData?.error_in_deleting_application);
        }
        console.log('error', error);
      } finally {
        setLoading(false);
      }
    } else {
      console.log('Wrong value selected');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="flex max-w-[639px] flex-col justify-between border-link-active bg-gradient-sky-dark p-8"
        aria-describedby={undefined}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 border-b border-line-primary pb-5 text-2xl font-normal">
            <Image src={Alert} alt="alert icon" className="h-8 w-8" />
            {isWorkflow
              ? langData?.delete_component
              : langData?.delete_application}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex max-h-40 min-h-20 items-center justify-center">
            <Loader
              title={messages?.General.deleting}
              description={messages?.General.please_wait}
            />
          </div>
        ) : (
          <div className="text-sm font-normal">
            <p>
              {langData?.are_you_sure_you_want_to_delete_the} “{title}”{' '}
              {isWorkflow ? langData?.component : langData?.application}?
            </p>
            <p className="mb-6 mt-1">
              {langData?.this_action_cannot_be_undone}
            </p>

            {(dataLen && dataLen > 0) ||
              (showRadio && showRadio && (
                <>
                  {isWorkflow && (
                    <p className="text-body text-primary">
                      <span className="font-bold lowercase">
                        {dataLen} {messages?.General?.traces}
                      </span>{' '}
                      {messages?.General?.found_related_to_this_component}
                    </p>
                  )}
                  <RadioGroup
                    defaultValue="delete only app"
                    className="mt-4"
                    onValueChange={(value) => setRadioSelected(value)}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="delete only app"
                        id="r1"
                        className={`bg-canvas-background-primary ${radioSelected === 'delete only app' && 'border-none bg-fill-button-rest'}`}
                      />
                      <Label htmlFor="r1" className="text-[13px] font-normal">
                        {isWorkflow
                          ? langData?.delete_component_but_keep_associated_traces
                          : langData?.delete_application_but_keep_associated_data}
                      </Label>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex w-fit cursor-not-allowed items-center space-x-2 opacity-60">
                            <RadioGroupItem
                              value="delete app and data"
                              id="r2"
                              className={`bg-canvas-background-primary ${radioSelected === 'delete app and data' && 'border-none bg-fill-button-rest'}`}
                              disabled
                            />
                            <Label
                              htmlFor="r2"
                              className="flex items-center gap-2 text-[13px] font-normal"
                            >
                              <span>
                                {isWorkflow
                                  ? langData?.delete_component_and_all_its_traces
                                  : langData?.delete_application_and_all_its_data}
                              </span>
                              <span className="-ml-0.5 rounded bg-[#EFDA1F] px-1.5 text-btn-label-sm text-[#07181E]">
                                {messages?.General?.wip}
                              </span>
                            </Label>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          align="center"
                          className="max-w-52 rounded border-transparent bg-fill-form-control-selected"
                        >
                          {isWorkflow
                            ? langData?.component_tooltip_message
                            : langData?.application_tooltip_message}
                          <TooltipArrow className="fill-fill-form-control-selected" />
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </RadioGroup>
                </>
              ))}
          </div>
        )}

        <div className="flex items-center gap-3 border-t border-t-line-primary pt-5">
          <CustomButton
            type="submit"
            customSize="regular"
            customVariant="primary"
            onClick={handleDelete}
            disabled={loading}
          >
            {messages?.General?.delete}
          </CustomButton>

          <CustomButton
            customSize="regular"
            customVariant="secondary"
            onClick={() => setOpen(false)}
          >
            {messages?.General?.cancel}
          </CustomButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
